import React, { useState, useEffect } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { AlertTriangle, Calendar, FileText, User, Eye, Clock, Send } from 'lucide-react';
import ResponsiveTooltip from '../ui/ResponsiveTooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { format } from 'date-fns';
import { truncateText } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { showToast } from '../../utils/toast';
import { handleAPIError } from '../../utils/errorHandler';
import FileUploadModal from '../FileUploadModal';
import CommentModal from '../CommentModal';
import { actionPointAPI } from '../../Services/apiService';
import Pagination from '../Pagination';

const MyOverdueTasksTable = ({
  tasks = [],
  page,
  pages,
  total,
  pageSize,
  hasNext,
  hasPrev,
  onPageChange,
  onPageSizeChange
}) => {
  const { user } = useAuth();
  const [viewCommentOpen, setViewCommentOpen] = useState(false);
  const [viewCommentText, setViewCommentText] = useState('');
  const [fileUploadModalOpen, setFileUploadModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reuploadedTasks, setReuploadedTasks] = useState(new Set());
  // Sorting state
  const [sortColumn, setSortColumn] = useState('overdue_days'); // Default sort by overdue days
  const [sortDirection, setSortDirection] = useState('desc'); // desc = most overdue first

  // Persist reuploaded tasks state when tasks refresh
  useEffect(() => {
    setReuploadedTasks(prev => {
      const newSet = new Set();
      tasks.forEach(task => {
        if (task.status === 'rejected_in_review' && prev.has(task.id)) {
          newSet.add(task.id);
        }
      });
      return newSet;
    });
  }, [tasks]);

  const handleSubmitForReview = (task) => {
    if (task.document_required && !task.document_url) {
      showToast.error('Document upload is required before submission');
      return;
    }

    setSelectedTask(task);
    setCommentModalOpen(true);
  };

  const handleCommentSubmit = async (comment) => {
    if (!selectedTask) return;

    setIsSubmitting(true);
    try {
      await actionPointAPI.submitForReview(selectedTask.id, comment);
      showToast.success('Task submitted for review');

      if (selectedTask.status === 'rejected_in_review') {
        setReuploadedTasks(prev => {
          const newSet = new Set(prev);
          newSet.delete(selectedTask.id);
          return newSet;
        });
      }

      setCommentModalOpen(false);
      setSelectedTask(null);

      // Trigger parent refresh without page reload
      window.dispatchEvent(new CustomEvent('taskStatusChanged'));
    } catch (error) {
      handleAPIError(error, 'Failed to submit task for review', true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUploadDocument = (task) => {
    setSelectedTask(task);
    setFileUploadModalOpen(true);
  };

  const handleFileUploadSuccess = () => {
    setFileUploadModalOpen(false);
    showToast.success('Document uploaded successfully');

    // If this was a rejected task, mark it as re-uploaded
    if (selectedTask && selectedTask.status === 'rejected_in_review') {
      setReuploadedTasks(prev => new Set(prev).add(selectedTask.id));
    }

    // Trigger parent refresh without page reload
    window.dispatchEvent(new CustomEvent('taskStatusChanged'));
  };
  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  const getStatusBadge = (status) => {
    // All overdue tasks show as "Overdue" badge
    return <Badge variant="destructive">Overdue</Badge>;
  };

  function calculateOverdueDays(task) {
    const dueDate = new Date(task.due_date);
    dueDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Calculate days overdue (negative number)
    const diffTime = dueDate - today;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays; // Will be negative for overdue tasks
  }

  // Handle sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection(column === 'overdue_days' ? 'desc' : 'asc');
    }
  };

  // Get sort icon
  const getSortIcon = (column) => {
    if (sortColumn === column) {
      return (
        <span className="ml-1 text-gray-400">
          {sortDirection === 'asc' ? '▲' : '▼'}
        </span>
      );
    }
    return null;
  };

  // Sort tasks based on selected column
  const sortedTasks = [...tasks].sort((a, b) => {
    let compareValue = 0;

    if (sortColumn === 'overdue_days') {
      const daysA = calculateOverdueDays(a);
      const daysB = calculateOverdueDays(b);
      // More negative = more overdue, so reverse for descending
      compareValue = daysB - daysA; // -5 comes before -1
    } else if (sortColumn === 'due_date') {
      const dateA = a.due_date ? new Date(a.due_date).getTime() : 0;
      const dateB = b.due_date ? new Date(b.due_date).getTime() : 0;
      compareValue = dateA - dateB;
    } else if (sortColumn === 'task') {
      const descA = (a.description || a.title || '').toLowerCase();
      const descB = (b.description || b.title || '').toLowerCase();
      compareValue = descA.localeCompare(descB);
    }

    if (compareValue === 0) {
      const idA = typeof a.id === 'number' ? a.id : parseInt(a.id);
      const idB = typeof b.id === 'number' ? b.id : parseInt(b.id);
      if (!isNaN(idA) && !isNaN(idB)) {
        compareValue = idA - idB;
      } else {
        compareValue = String(a.id).localeCompare(String(b.id));
      }
    }

    return sortDirection === 'asc' ? compareValue : -compareValue;
  });

  if (sortedTasks.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-8">
          <AlertTriangle className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Overdue Tasks</h3>
          <p className="text-muted-foreground text-center">
            Great! You don't have any overdue tasks.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent>
          {/* Desktop Table View */}
          <div className="hidden md:block border rounded-lg mt-2 overflow-hidden">
            <div className="max-h-[calc(100vh-290px)] overflow-auto">
              <div className="relative w-full overflow-visible">
                <table className="w-full caption-bottom text-sm">
                  <thead className="sticky top-0 z-10 bg-gray-100 shadow-sm [&_tr]:border-b">
                    <tr className='bg-gray-100 border-b'>
                      <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Sr. No.</th>
                      <th
                        className="h-10 px-2 text-left align-left font-medium text-muted-foreground cursor-pointer hover:bg-gray-200 transition-colors"
                        onClick={() => handleSort('task')}
                      >
                        <div className="flex items-center justify-left">
                          Task
                          {getSortIcon('task')}
                        </div>
                      </th>
                      <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Approver</th>
                      <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Status</th>
                      <th
                        className="h-10 px-2 text-center align-middle font-medium text-muted-foreground cursor-pointer hover:bg-gray-200 transition-colors"
                        onClick={() => handleSort('due_date')}
                      >
                        <div className="flex items-center justify-center gap-2">
                          Due Date
                          <Calendar className="w-4 h-4 text-gray-400" />
                          {getSortIcon('due_date')}
                        </div>
                      </th>
                      <th
                        className="h-10 px-2 text-center align-middle font-medium text-muted-foreground cursor-pointer hover:bg-gray-200 transition-colors"
                        onClick={() => handleSort('overdue_days')}
                      >
                        <div className="flex items-center justify-center">
                          Overdue Days
                          {getSortIcon('overdue_days')}
                        </div>
                      </th>
                      <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground">Remark</th>
                      <th className="h-10 px-2 text-center align-middle font-medium text-muted-foreground w-10">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="[&_tr:last-child]:border-0">
                    {sortedTasks.map((task, index) => {
                      const overdueDays = calculateOverdueDays(task);

                      return (
                        <tr key={task.id} className="border-b transition-colors hover:bg-gray-50">
                          <td className="p-2 text-center align-middle">
                            {(page - 1) * pageSize + index + 1}
                          </td>
                          <td className="p-2 text-left align-left">
                            <div className="space-y-1">
                              {(task.description || task.title) && (task.description || task.title).length > 20 ? (
                                <ResponsiveTooltip
                                  content={<p className="text-sm whitespace-pre-wrap">{task.description || task.title}</p>}
                                  className="max-w-md p-3"
                                >
                                  <div className="font-medium cursor-pointer mx-auto">
                                    {truncateText(task.description || task.title, 20)}
                                  </div>
                                </ResponsiveTooltip>
                              ) : (
                                <div className="font-medium mx-auto">{task.description || task.title}</div>
                              )}
                              {task.meeting?.title && (
                                task.meeting.title.length > 20 ? (
                                  <ResponsiveTooltip
                                    content={<p className="max-w-xs whitespace-pre-wrap">{task.meeting.title}</p>}
                                  >
                                    <span className="text-sm text-gray-600 cursor-pointer">
                                      Meeting: {truncateText(task.meeting.title, 20)}
                                    </span>
                                  </ResponsiveTooltip>
                                ) : (
                                  <span className="text-sm text-gray-600">
                                    Meeting: {task.meeting.title}
                                  </span>
                                )
                              )}
                            </div>
                          </td>
                          <td className="p-2 text-center align-middle">
                            <div className="text-sm text-gray-900">
                              {task.approver?.full_name || 'Not Assigned'}
                            </div>
                          </td>
                          <td className="p-2 text-center align-middle">
                            {getStatusBadge(task.status)}
                          </td>
                          <td className="p-2 text-center align-middle">
                            <div className="flex items-center justify-center gap-2">
                              {formatDate(task.due_date)}
                            </div>
                          </td>
                          <td className="p-2 text-center align-middle">
                            <Badge variant="destructive">
                              {overdueDays}
                            </Badge>
                          </td>
                          <td className="p-2 text-center align-middle">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setViewCommentText(task.review_comment || 'No remark available');
                                setViewCommentOpen(true);
                              }}
                              disabled={!task.review_comment}
                              className={task.review_comment ? "text-red-600 hover:text-red-700" : "text-gray-400 cursor-not-allowed"}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                            </Button>
                          </td>
                          <td className="p-2 text-center align-middle">
                            <div className="flex items-center justify-center gap-2">
                              {task.status === 'pending' && task.document_required && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleUploadDocument(task)}
                                  >
                                    <FileText className="h-4 w-4 mr-1" />
                                    {task.document_url ? "Re-upload Doc" : "Upload Doc"}
                                  </Button>
                                  {task.document_url && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => window.open(task.document_url, '_blank')}
                                    >
                                      <FileText className="h-4 w-4 mr-1" />
                                      View
                                    </Button>
                                  )}
                                  
                                </>
                              )}

                              {task.status === 'pending' && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleSubmitForReview(task)}
                                    className="text-blue-600 hover:text-blue-700"
                                    disabled={task.document_required && !task.document_url}
                                  >
                                    <Send className="h-4 w-4 mr-1" />
                                    Submit
                                  </Button>
                                </>
                              )}

                              {task.status === 'rejected_in_review' && (
                                <>
                                  {task.document_required && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleUploadDocument(task)}
                                      className="text-orange-600  hover:text-orange-700"
                                    >
                                      <FileText className="h-4 w-4 mr-1" />
                                      {task.document_url ? "Re-upload Doc" : "Upload Doc"}
                                    </Button>
                                  )}
                                  {task.document_url && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => window.open(task.document_url, '_blank')}
                                    >
                                      <FileText className="h-4 w-4 mr-1" />
                                      View
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleSubmitForReview(task)}
                                    className="text-green-600 hover:text-green-700"
                                    disabled={task.document_required && !reuploadedTasks.has(task.id)}
                                  >
                                    <Send className="h-4 w-4 mr-1" />
                                    Resubmit
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Mobile Accordion View */}
          <div className="md:hidden mt-2">
            <Accordion type="single" collapsible className="w-full space-y-2">
              {sortedTasks.map((task, index) => {
                const overdueDays = calculateOverdueDays(task);

                return (
                  <AccordionItem key={task.id} value={`overdue-${task.id}`} className="border rounded-lg px-4 bg-white">
                    <AccordionTrigger className="hover:no-underline py-4">
                      <div className="flex items-center justify-between w-full pr-4">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span className="text-sm font-medium text-gray-500 flex-shrink-0">
                            {(page - 1) * pageSize + index + 1}.
                          </span>
                          <div className="flex-1 min-w-0">
                            {(task.description || task.title) && (task.description || task.title).length > 20 ? (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <span className="text-sm font-medium text-gray-900 text-left hover:underline cursor-pointer inline-block">
                                    {truncateText(task.description || task.title, 20)}
                                  </span>
                                </PopoverTrigger>
                                <PopoverContent className="w-72 max-h-[200px] p-3 overflow-y-auto" align="start">
                                  <div className="text-sm font-medium text-gray-900 whitespace-pre-wrap">
                                    {task.description || task.title}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            ) : (
                              <p className="text-sm font-medium text-gray-900">{task.description || task.title}</p>
                            )}

                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-4 pt-0">
                      <div className="space-y-4  pt-2 border-t">
                        {task.meeting?.title && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Meeting</p>
                            {task.meeting.title.length > 20 ? (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <span className="text-sm text-gray-900 text-left hover:underline cursor-pointer inline-block">
                                    {truncateText(task.meeting.title, 20)}
                                  </span>
                                </PopoverTrigger>
                                <PopoverContent className="w-72 p-3" align="start">
                                  <div className="text-sm text-gray-900 whitespace-pre-wrap">
                                    {task.meeting.title}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            ) : (
                              <p className="text-sm text-gray-900">{task.meeting.title}</p>
                            )}
                          </div>
                        )}
                        <div className='flex flex-wrap justify-between'>
                          <div >
                            <p className="text-xs font-medium text-gray-500 mb-1">Approver</p>
                            <p className="text-sm text-gray-900">{task.approver?.full_name || 'Not Assigned'}</p>
                          </div>

                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Due Date</p>
                            <p className="text-sm text-gray-900">{formatDate(task.due_date)}</p>
                          </div>

                          <div className='flex flex-col justify-center items-center'>
                            <p className="text-xs  font-medium text-gray-500 mb-1">Overdue Days</p>
                            <Badge variant="destructive" className="text-xs">
                              {overdueDays}
                            </Badge>
                          </div>
                        </div>

                        {task.review_comment && (
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-1">Remark</p>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setViewCommentText(task.review_comment || 'No remark available');
                                setViewCommentOpen(true);
                              }}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View Remark
                            </Button>
                          </div>
                        )}
                        <div className=''>
                          <p className="text-xs  font-medium text-gray-500 mb-1">Actions </p>
                          <div className="flex flex-wrap  gap-2">
                            {task.status === 'pending' && task.document_required && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleUploadDocument(task)}
                                >
                                  <FileText className="h-4 w-4 mr-1" />
                                  {task.document_url ? 'Re-upload Doc' : 'Upload Doc'}
                                </Button>
                                {task.document_url && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => window.open(task.document_url, '_blank')}
                                  >
                                    <FileText className="h-4 w-4 mr-1" />
                                    View Doc
                                  </Button>
                                )}
                                
                              </>
                            )}

                             {task.status === 'pending' && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleSubmitForReview(task)}
                                    className="text-blue-600 hover:text-blue-700"
                                    disabled={task.document_required && !task.document_url}
                                  >
                                    <Send className="h-4 w-4 mr-1" />
                                    Submit
                                  </Button>
                                </>
                              )}
                              
                            {task.status === 'rejected_in_review' && (
                              <>
                                {task.document_required && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleUploadDocument(task)}
                                    className="text-orange-600 hover:text-orange-700"
                                  >
                                    <FileText className="h-4 w-4 mr-1" />
                                    Re-upload Doc
                                  </Button>
                                )}
                                {task.document_url && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => window.open(task.document_url, '_blank')}
                                  >
                                    <FileText className="h-4 w-4 mr-1" />
                                    View
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleSubmitForReview(task)}
                                  className="text-green-600 hover:text-green-700"
                                  disabled={task.document_required && !reuploadedTasks.has(task.id)}
                                >
                                  <Send className="h-4 w-4 mr-1" />
                                  Resubmit
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>

          {/* Pagination */}
          {total > 0 && (
            <Pagination
              page={page}
              pages={pages}
              total={total}
              pageSize={pageSize}
              hasNext={hasNext}
              hasPrev={hasPrev}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={viewCommentOpen} onOpenChange={() => setViewCommentOpen(false)}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Remark:</DialogTitle>
          </DialogHeader>
          <div className="whitespace-pre-wrap text-sm text-foreground">
            {viewCommentText}
          </div>
        </DialogContent>
      </Dialog>

      <FileUploadModal
        open={fileUploadModalOpen}
        onClose={() => {
          setFileUploadModalOpen(false);
          setSelectedTask(null);
        }}
        task={selectedTask}
        onUploadSuccess={handleFileUploadSuccess}
      />

      <CommentModal
        open={commentModalOpen}
        onClose={() => {
          setCommentModalOpen(false);
          setSelectedTask(null);
        }}
        onSubmit={handleCommentSubmit}
        title="Submit for Review"
        placeholder="Add a remark about your submission (optional)..."
        submitText="Submit"
        isLoading={isSubmitting}
      />
    </>
  );
};

export default MyOverdueTasksTable;